import { useState } from 'react'
import { MdAdd, MdRefresh } from 'react-icons/md'
import { Button, HStack, Icon, useDisclosure } from '@ritualco/jutsu'
import {
  useCreateWorkspaceConnections,
  useDeleteWorkspaceConnection,
  useGetWorkspaceConnections,
  useResendWorkspaceConnection,
  useResendWorkspaceConnections,
} from 'api/endpoints/connections'
import { EditableSection } from 'components/EditableSection'
import { CompanyInviteModal } from 'components/Modals/CompanyInviteModal'
import { Pagination } from 'components/Pagination'
import { CompaniesTable } from 'components/Tables/CompaniesTable'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'
import Loading from 'components/Loading'
import { INITIAL_ROWS_PER_PAGE } from 'config'

type CompaniesProps = {
  organizationId: string
  workspaceId: string
}

const Companies = ({ workspaceId, organizationId }: CompaniesProps) => {
  const { formatMessage } = useContent()
  const { addMessage } = useMessage()
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginationTokenOption, setPaginationTokenOption] = useState({})
  const { status, data } = useGetWorkspaceConnections({
    organizationId,
    workspaceId,
    limit: rowsPerPage,
    ...paginationTokenOption,
  })
  const create = useCreateWorkspaceConnections()
  const remove = useDeleteWorkspaceConnection()
  const resend = useResendWorkspaceConnection()
  const resendAll = useResendWorkspaceConnections()
  const { isOpen, onOpen, onClose } = useDisclosure()

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  const showResendAllButton = data?.results.some(
    invitation => invitation.status === 'PENDING'
  )
  const handleRemove = (connectionId: string) => {
    remove.mutate(
      { organizationId, workspaceId, connectionId },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.companies.removeSuccess',
            }),
            isClosable: false,
          })
        },
      }
    )
  }
  const handleResend = (connectionId: string) => {
    resend.mutate(
      { organizationId, workspaceId, connectionId },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.companies.resendSuccess',
            }),
            isClosable: false,
          })
        },
      }
    )
  }
  const handleResendAll = () => {
    resendAll.mutate(
      { organizationId, workspaceId },
      {
        onSuccess: data => {
          const count = data.results?.length || 0
          addMessage({
            status: 'success',
            description: formatMessage(
              { id: 'pmportal.companies.resendAllSuccess' },
              { count }
            ),
            isClosable: false,
          })
        },
      }
    )
  }

  return (
    <>
      <EditableSection
        title={formatMessage({ id: 'pmportal.companies.companiesTableTitle' })}
        action={
          <HStack>
            {showResendAllButton && (
              <Button
                colorScheme="transparent"
                variant="outline"
                leftIcon={<Icon as={MdRefresh} w="18px" h="18px" />}
                isLoading={resendAll.isLoading}
                onClick={handleResendAll}
              >
                {formatMessage({ id: 'pmportal.companies.resendAll' })}
              </Button>
            )}
            <Button
              colorScheme="transparent"
              variant="outline"
              leftIcon={<Icon as={MdAdd} w="18px" h="18px" />}
              onClick={onOpen}
            >
              {formatMessage({ id: 'pmportal.companies.inviteCompanies' })}
            </Button>
          </HStack>
        }
      >
        <CompaniesTable
          workspaceConnections={data?.results}
          onResend={handleResend}
          onRemove={handleRemove}
        />
        <Pagination
          rowsPerPage={rowsPerPage}
          hasNext={data?.hasNextPage}
          hasPrev={data?.hasPreviousPage}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          onPrev={() =>
            setPaginationTokenOption({
              previousPageToken: data?.paginationToken,
            })
          }
          onNext={() =>
            setPaginationTokenOption({ nextPageToken: data?.paginationToken })
          }
        />
      </EditableSection>
      <CompanyInviteModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={emails => {
          create.mutate({
            organizationId,
            workspaceId,
            workspaceConnectionInvite: { emails },
          })
        }}
      />
    </>
  )
}
export default Companies
